import { createSlice } from "@reduxjs/toolkit";
import { AccountState } from "./account.types";
import {
    discoverAccountsThunk,
    getIdentifiersThunk,
    linkAccountThunk,
    verifyLinkAccountThunk,
    fetchDashboardDataThunk,
} from "./accountThunk";

const initialState: AccountState = {
    loading: false,
    accounts: [],
    linkedAccounts: [],
    selectedAccountsToLink: [],
    selectedFiTypes: [],
    linkRefNumber: undefined,
};

const accountSlice = createSlice({
    name: "account",
    initialState,
    reducers: {
        resetAccounts: () => initialState,
        setSelectedAccountsToLink: (state, action) => {
            state.selectedAccountsToLink = action.payload;
        },
        setSelectedFiTypes: (state, action) => {
            state.selectedFiTypes = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Get Identifiers (Background)
            .addCase(getIdentifiersThunk.fulfilled, (state, action) => {
                state.identifiers = action.payload.Identifiers || [];
            })

            // Discover
            .addCase(discoverAccountsThunk.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(discoverAccountsThunk.fulfilled, (state, action) => {
                state.loading = false;
                // fail-safe check
                const payload = action.payload as any;
                state.accounts = payload.ACC_LIST || [];
            })
            .addCase(discoverAccountsThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Link
            .addCase(linkAccountThunk.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(linkAccountThunk.fulfilled, (state, action) => {
                state.loading = false;
                // Store the accounts that were just linked
                // We passed them in meta.arg.accounts
                const linked = action.meta.arg.accounts;
                if (linked && linked.length > 0) {
                    // Filter out duplicates if needed, or just push
                    state.linkedAccounts.push(...linked);
                }
                const payload: any = action.payload || {};
                const safeRefNumber = payload.RefNumber || payload.refNumber || payload.REFNUMBER || payload.FIPACCREFNUM || payload.Ref_Number;
                if (safeRefNumber) {
                    state.linkRefNumber = safeRefNumber;
                }
            })
            .addCase(linkAccountThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Verify Link OTP
            .addCase(verifyLinkAccountThunk.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(verifyLinkAccountThunk.fulfilled, (state, action) => {
                state.loading = false;
                // Add newly linked accounts from response if available
                // CAMS might return them in fip_DiscoverLinkedlist OR fip_NewDiscoverelist
                const linkedList = action.payload.fip_DiscoverLinkedlist || action.payload.fip_NewDiscoverelist || [];

                // Extract the true FIPACCLINKREF from the inner objects if available
                let actualFipAccLinkRef = "";
                if (linkedList.length > 0 && linkedList[0].FIPACCLINKREF) {
                    actualFipAccLinkRef = linkedList[0].FIPACCLINKREF;
                }

                // Fallback sequence
                const rootLinkRef = actualFipAccLinkRef || action.payload.FIPACCLINKREF || action.payload.RefNumber || state.linkRefNumber || action.meta.arg.refNumber || "";

                const newLinked = linkedList.map((acc: any) => ({
                    ACC_ID: acc.FIPACCREFNUM || acc.FIPACCNUM,
                    MASKED_ACC_NO: acc.FIPACCNUM,
                    ACC_TYPE: acc.FIPACCTYPE,
                    FI_TYPE: acc.FITYPE,
                    LINKED: true,
                    // Additional fields for Consent Submission
                    FIPID: acc.FIPID || action.payload.FIPID,
                    FIPNAME: acc.FIPNAME || action.payload.FIPNAME,
                    FIPACCREFNUM: acc.FIPACCREFNUM || action.payload.FIPACCREFNUM,
                    LINKEDDATE: acc.LINKEDDATE || action.payload.LINKEDDATE,
                    FIPACCLINKREF: acc.FIPACCLINKREF || rootLinkRef,
                    CONSENTDATE: "", 
                    CUSTID: "" 
                }));

                state.linkedAccounts.push(...newLinked);

                // CRITICAL FIX: Ensure all previously tracked accounts get the confirmed FIPACCLINKREF!
                // CAMS sometimes returns empty fip_DiscoverLinkedlist here.
                if (rootLinkRef) {
                    state.linkedAccounts = state.linkedAccounts.map(acc => ({
                        ...acc,
                        FIPACCLINKREF: acc.FIPACCLINKREF || rootLinkRef
                    }));
                }
                // Also ensure we save the ref number from Verify Link if it's there
                if (action.meta.arg.refNumber) {
                    state.linkRefNumber = action.meta.arg.refNumber;
                }
            })
            .addCase(verifyLinkAccountThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Fetch Dashboard Data
            .addCase(fetchDashboardDataThunk.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(fetchDashboardDataThunk.fulfilled, (state, action) => {
                state.loading = false;
                // Safely extract linked accounts and consents from response
                // Response structure might vary depending on getConsentList_V1
                const payload = action.payload as any;
                if (payload.fip_DiscoverLinkedlist) {
                    state.linkedAccounts = payload.fip_DiscoverLinkedlist.map((acc: any) => ({
                        ACC_ID: acc.FIPACCREFNUM || acc.FIPACCNUM,
                        MASKED_ACC_NO: acc.FIPACCNUM,
                        ACC_TYPE: acc.FIPACCTYPE,
                        FI_TYPE: acc.FITYPE,
                        LINKED: true,
                        FIPID: acc.FIPID,
                        FIPNAME: acc.FIPNAME,
                        FIPACCREFNUM: acc.FIPACCREFNUM,
                        LINKEDDATE: acc.LINKEDDATE,
                        FIPACCLINKREF: acc.FIPACCLINKREF
                    }));
                }
                // We might want to store raw consents elsewhere if needed
            })
            .addCase(fetchDashboardDataThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { resetAccounts, setSelectedAccountsToLink, setSelectedFiTypes } = accountSlice.actions;
export default accountSlice.reducer;
